import API from '../../services/API'
import OT from '@opentok/client'
import { ActionContext } from 'vuex'
import { State } from './state'
import { BackstageSessionName } from '@/types/BackstageSession.type'

export function addPublisher(
    ctx: any,
    { sessionName, publisher }: { sessionName: BackstageSessionName; publisher: OT.Publisher }
) {
    publisher.on('streamCreated', () => {
        ctx.commit('ADD_PUBLISHER', { sessionName, publisher })
    })

    publisher.on('streamDestroyed', () => {
        ctx.commit('REMOVE_PUBLISHER', { sessionName, publisher })
    })

    if (publisher.session) {
        publisher.session.off('streamPropertyChanged')

        publisher.session.on('streamPropertyChanged', () => {
            ctx.commit('ADD_PUBLISHER', { sessionName, publisher })
        })
    }

    ctx.commit('ADD_PUBLISHER', { sessionName, publisher })
}

export function initSession(
    ctx: ActionContext<State, any>,
    { name, sessionId, sessionToken }: { name: 'live' | 'greenroom'; sessionId: string; sessionToken: string }
) {
    return new Promise((resolve, reject) => {
        const session: OT.Session = OT.initSession(process.env.VUE_APP_VONAGE_API_KEY as string, sessionId)

        session.on('sessionReconnecting', () => {
            ctx.commit('SESSION_STATUS_UPDATED', {
                session: name,
                status: 'reconnecting',
            })
        })

        session.on('sessionReconnected', () => {
            ctx.commit('SESSION_STATUS_UPDATED', {
                session: name,
                status: 'reconnected',
            })
        })

        session.on('sessionDisconnected', event => {
            ctx.commit('SESSION_STATUS_UPDATED', {
                session: name,
                status: 'disconnected',
            })

            if (event.reason === 'networkDisconnected') {
                ctx.dispatch('disconnectSession', { sessionName: name })
                ctx.dispatch('initSession', { name, sessionId, sessionToken })
            }
        })

        session.on('connectionCreated', event => {
            ctx.commit('ADD_CONNECTION', {
                sessionName: name,
                connection: event.connection,
            })
        })

        session.on('connectionDestroyed', event => {
            ctx.commit('REMOVE_CONNECTION', {
                sessionName: name,
                connection: event.connection,
            })
        })

        session.on('streamCreated', event => {
            const stream = event.stream
            ctx.commit('ADD_STREAM', { session: name, stream })
            ctx.commit('SET_AUDIO_VOLUME', {
                session,
                sessionName: name,
                volume: ctx.state[name].volume,
            })
        })

        session.on('streamDestroyed', event => {
            ctx.commit('REMOVE_STREAM', {
                session: name,
                stream: event.stream,
            })
        })

        session.connect(sessionToken, error => {
            if (error) {
                reject(error)
            }

            ctx.commit('INIT_SESSION', { name, session })
            resolve(session)
        })
    })
}

export function disconnectSession(
    ctx: ActionContext<State, any>,
    { sessionName }: { sessionName: BackstageSessionName }
) {
    const session = ctx.state[sessionName].session

    session?.off()
    session?.disconnect()

    ctx.commit('REMOVE_SESSION', { sessionName })
}

export async function handleLayout(ctx: any, eventId: string | number) {
    const subscriberStreams: OT.Stream[] = [...ctx.state.live.streams]
    const publishersStream = ctx.state.live.publishers.map((publisher: OT.Publisher) => publisher.stream)

    const hiddenStreams = [...subscriberStreams, ...publishersStream]
        .filter((stream: OT.Stream) => {
            return stream && stream.videoType !== 'screen' && !stream.hasVideo
        })
        .map((stream: OT.Stream) => stream.streamId)

    await API.updateBroadcastLayout(eventId, hiddenStreams)
}

export async function forceLayout(ctx: any, { eventId, layout }: { eventId: string | number; layout: string }) {
    await API.forceBroadcastLayout(eventId, layout)
}
